import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Clock3, MapPin, PlayCircle, X } from 'lucide-react';
import { globeAssets } from './constants';
import type { CompanionCity } from './types';

type CompanionVideoModalProps = {
  city: CompanionCity | null;
  onClose: () => void;
};

const CompanionVideoModal: React.FC<CompanionVideoModalProps> = ({ city, onClose }) => {
  useEffect(() => {
    if (!city) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [city, onClose]);

  return (
    <AnimatePresence>
      {city ? (
        <motion.div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/75 p-4 backdrop-blur-md sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="companion-video-title"
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            onClick={(event) => event.stopPropagation()}
            className="relative w-full max-w-2xl overflow-hidden rounded-[var(--cp-radius-xl)] border border-white/10 bg-[#07101f] text-right text-white shadow-[0_40px_100px_-48px_rgba(28,59,155,0.65)]"
          >
            <div
              className="pointer-events-none absolute -left-24 -top-24 h-56 w-56 rounded-full opacity-35 blur-3xl"
              style={{ backgroundColor: city.tone }}
            />
            <button
              type="button"
              onClick={onClose}
              className="absolute left-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/12 bg-black/30 text-white/80 transition hover:bg-white/10 hover:text-white"
              aria-label="بستن"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-[var(--cp-brand)]/40 via-slate-900 to-[var(--cp-accent)]/30">
              <div
                className="absolute inset-0 bg-cover bg-center opacity-25"
                style={{ backgroundImage: `url(${globeAssets.night})` }}
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <button
                  type="button"
                  className="group flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-full border-2 border-white/25 bg-white/10 text-white shadow-xl backdrop-blur transition hover:scale-105 hover:border-white/40"
                  aria-label="پخش ویدیو"
                >
                  <PlayCircle className="h-11 w-11 transition group-hover:text-cyan-200" />
                </button>
                <span className="rounded-full border border-white/15 bg-black/35 px-3 py-1 text-[11px] font-bold text-white/80">
                  پیش‌نمایش · {city.videoDuration}
                </span>
              </div>
            </div>

            <div className="relative space-y-3 p-5 sm:p-6">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-black text-white"
                  style={{ backgroundColor: city.tone }}
                >
                  <MapPin className="h-3 w-3" />
                  {city.city}، {city.country}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-bold text-white/70">
                  <Clock3 className="h-3.5 w-3.5" />
                  {city.videoDuration}
                </span>
              </div>
              <h3 id="companion-video-title" className="text-xl font-black sm:text-2xl">
                {city.videoTitle}
              </h3>
              <p className="text-sm leading-7 text-white/65">{city.story}</p>
              <p className="rounded-xl border border-white/8 bg-white/5 px-3 py-2.5 text-xs leading-6 text-white/50">
                این بخش فعلاً نمایشی است؛ پس از آماده‌شدن ویدیوهای واقعی هر شهر، پخش‌کننده به
                فایل اختصاصی وصل می‌شود.
              </p>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default CompanionVideoModal;
